import { addDays, addYears, getYear, isBefore } from 'date-fns'
import { batch } from 'react-redux'
import { customizeError } from '@chilecompra/react-kit/errors'
import { formatDNI } from '@chilecompra/react-kit/utils'
import { deleteCookie } from '@chilecompra/react-kit'
import {
  isDevEnv,
  isLocalEnv,
  REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY,
  REACT_APP_FEATURE_FLAG_TAXES_KEY_NATURAL_CHECKS
} from '../../config/settings/environment'
import { makeActionCreator } from '../../config/store/utils'
import {
  getBusinessAreas,
  getBusinessIncomeBrackets,
  getCountries,
  getDistricts,
  getStates
} from '../../services/config'
import {
  checkSellerIsBlacklisted,
  getSeller,
  getSellerActivities,
  getSellerAddress,
  getSellerBusiness,
  getSellerIncomeBracket,
  getSellerIncomeSales,
  postBlockedInformation,
  signAffidavit
} from '../../services/sellers'
import {
  associateAdmin,
  getIdentity,
  validateDni,
  codeValidation,
  createSeller,
  createTemporarySeller,
  createTemporarySellerAreas,
  sendRegistrationEmail,
  recoverAdmin,
  resendRegistrationEmail,
  updateRegistrationInformation,
  updatePassword
} from '../../services/register'
import { CACHE_CONFIG, DNI_TEST } from '../../config/settings/constants'
import { checkIfForeignDni, checkIfIsMinor } from '../../modules/utils/helpers'
import { setItem, setItemWithExpiryAt } from '../../modules/utils/localStorage'

const isEnableCivilRegistry =
  REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY === true || REACT_APP_FEATURE_FLAG_ENABLE_CIVIL_REGISTRY === 'true'

function getErrorCode(response) {
  return response?.errores?.[0]?.codigo ?? null
}

const ffTaxesKeyNaturalChecks =
  REACT_APP_FEATURE_FLAG_TAXES_KEY_NATURAL_CHECKS === true || REACT_APP_FEATURE_FLAG_TAXES_KEY_NATURAL_CHECKS === 'true'

export const GET_GUEST_FULL_NAME = 'GET_GUEST_FULL_NAME'
export const GET_GUEST_FULL_NAME_ERROR = 'GET_GUEST_FULL_NAME_ERROR'
export const GET_GUEST_FULL_NAME_SUCCESS = 'GET_GUEST_FULL_NAME_SUCCESS'
export const onGetGuestFullName = makeActionCreator(GET_GUEST_FULL_NAME)
export const onGetGuestFullNameError = makeActionCreator(GET_GUEST_FULL_NAME_ERROR, 'payload')
export const onGetGuestFullNameSuccess = makeActionCreator(GET_GUEST_FULL_NAME_SUCCESS, 'payload')
export const onGetGuestFullNameThunk = payload => async dispatch => {
  dispatch(onGetGuestFullName())

  try {
    if (isDevEnv && DNI_TEST.includes(payload.dni)) {
      return batch(() => {
        dispatch(
          onGetGuestFullNameSuccess({
            dni: payload.dni,
            firstLastName: 'Del Rio',
            gender: 2,
            name: 'Maria Carmen de los Angeles',
            secondLastName: 'Gonzalez'
          })
        )
        payload.callback()
      })
    }
    const { data } = await getIdentity({ dni: formatDNI(payload.dni, 2) })

    return batch(() => {
      dispatch(
        onGetGuestFullNameSuccess({
          dni: payload.dni,
          firstLastName: data.payload?.apellidoPaterno,
          gender: data.payload?.sexo === 'F' ? 2 : 1,
          name: data.payload?.nombres,
          secondLastName: data.payload?.apellidoMaterno || ''
        })
      )
      payload.callback()
    })
  } catch (error) {
    if (error.originalError?.response?.data?.errores[0]?.codigo === '400-6') {
      return dispatch(onGetGuestFullNameError(customizeError({ code: 400, reason: 'DNI_BELONGS_TO_A_DECEASED_ERROR' })))
    }

    return dispatch(onGetGuestFullNameError(error.toObject()))
  }
}

export const SAVE_TEMPORARY_PERSONAL_INFORMATION = 'SAVE_TEMPORARY_PERSONAL_INFORMATION'
export const onSaveTemporaryPersonalInformation = makeActionCreator(SAVE_TEMPORARY_PERSONAL_INFORMATION, 'payload')

export const RECOVER_ADMIN = 'RECOVER_ADMIN'
export const RECOVER_ADMIN_ERROR = 'RECOVER_ADMIN_ERROR'
export const RECOVER_ADMIN_SUCCESS = 'RECOVER_ADMIN_SUCCESS'
export const onRecoverAdmin = makeActionCreator(RECOVER_ADMIN)
export const onRecoverAdminError = makeActionCreator(RECOVER_ADMIN_ERROR, 'payload')
export const onRecoverAdminSuccess = makeActionCreator(RECOVER_ADMIN_SUCCESS)
export const onRecoverAdminThunk = payload => async (dispatch, getState) => {
  dispatch(onRecoverAdmin())

  try {
    const { id } = getState().registerWizard.guest
    const { response } = await recoverAdmin({ id })

    return batch(() => {
      dispatch(onRecoverAdminSuccess({ payload, response }))
      dispatch(onAssociateAdminThunk({ callback: () => payload.callback() }))
    })
  } catch (error) {
    return dispatch(onRecoverAdminError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
  }
}

export const ASSOCIATE_ADMIN = 'ASSOCIATE_ADMIN'
export const ASSOCIATE_ADMIN_ERROR = 'ASSOCIATE_ADMIN_ERROR'
export const ASSOCIATE_ADMIN_SUCCESS = 'ASSOCIATE_ADMIN_SUCCESS'
export const onAssociateAdmin = makeActionCreator(ASSOCIATE_ADMIN)
export const onAssociateAdminError = makeActionCreator(ASSOCIATE_ADMIN_ERROR, 'payload')
export const onAssociateAdminSuccess = makeActionCreator(ASSOCIATE_ADMIN_SUCCESS)
export const onAssociateAdminThunk = payload => async (dispatch, getState) => {
  dispatch(onAssociateAdmin())

  try {
    const { guest, business, isTaxesKey, isUniquePass } = getState().registerWizard
    const dniUser = guest.dni
    const dniBusiness = business.dni
    const id = guest.id
    const { response } = await associateAdmin({ dniUser, dniBusiness, id, isUniquePass, isTaxesKey })

    return batch(() => {
      dispatch(onAssociateAdminSuccess({ payload, response }))
      payload.callback()
    })
  } catch (error) {
    if (error.originalError?.response?.data?.errores[0]?.codigo === '400-7') {
      return dispatch(onAssociateAdminError(customizeError({ code: 400, reason: 'YOU_ARE_ALREADY_ADMIN_ERROR' })))
    }
    if (error.originalError?.response?.data?.errores[0]?.codigo === '400-8') {
      return dispatch(onAssociateAdminError(customizeError({ code: 400, reason: 'USER_NOT_FOUND_BY_DNI_ERROR' })))
    }
    if (error.originalError?.response?.data?.errores[0]?.codigo === '400-9') {
      return dispatch(onAssociateAdminError(customizeError({ code: 400, reason: 'USER_NOT_FOUND_ERROR' })))
    }
    if (error.originalError?.response?.data?.errores[0]?.codigo === '400-15') {
      return dispatch(onAssociateAdminError(customizeError({ code: 400, reason: 'SELLER_NOT_FOUND_ERROR' })))
    }

    return dispatch(onAssociateAdminError(customizeError({ code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
  }
}

export const VALIDATE_DNI = 'VALIDATE_DNI'
export const VALIDATE_DNI_ERROR = 'VALIDATE_DNI_ERROR'
export const VALIDATE_DNI_SUCCESS = 'VALIDATE_DNI_SUCCESS'
export const onValidateDni = makeActionCreator(VALIDATE_DNI)
export const onValidateDniError = makeActionCreator(VALIDATE_DNI_ERROR, 'payload')
export const onValidateDniSuccess = makeActionCreator(VALIDATE_DNI_SUCCESS)
export const onValidateDniThunk = payload => async dispatch => {
  dispatch(onValidateDni())

  try {
    const { dni, documentNumber } = payload
    const response = await validateDni({ dni, documentNumber })
    const { estadoRespuesta: statusResponse } = response.data.payload

    // Dni or number of documents do not correspond.
    if (statusResponse === 301 && !(isDevEnv && DNI_TEST.includes(dni))) {
      throw customizeError({ code: 301, reason: 'DNI_NUMBER_INCORRECT_ERROR' })
    }

    return batch(() => {
      dispatch(onValidateDniSuccess())
      dispatch(onGetGuestFullNameThunk({ ...payload, dni }))
    })
  } catch (error) {
    return dispatch(onValidateDniError(error.toObject()))
  }
}

export const SEND_REGISTRATION_EMAIL = 'SEND_REGISTRATION_EMAIL'
export const SEND_REGISTRATION_EMAIL_ERROR = 'SEND_REGISTRATION_EMAIL_ERROR'
export const SEND_REGISTRATION_EMAIL_SUCCESS = 'SEND_REGISTRATION_EMAIL_SUCCESS'
export const onSendRegistrationEmail = makeActionCreator(SEND_REGISTRATION_EMAIL)
export const onSendRegistrationEmailError = makeActionCreator(SEND_REGISTRATION_EMAIL_ERROR, 'payload')
export const onSendRegistrationEmailSuccess = makeActionCreator(SEND_REGISTRATION_EMAIL_SUCCESS, 'payload')
export const onSendRegistrationEmailThunk = payload => async (dispatch, getState) => {
  dispatch(onSendRegistrationEmail())

  try {
    const { guest, registerType } = getState().registerWizard
    const isUniquePass = registerType === 'by-unique-pass'

    if (isLocalEnv) {
      return batch(() => {
        dispatch(
          onSendRegistrationEmailSuccess({
            id: 'local-send-email-test',
            code: '1234',
            email: payload.email,
            isForeign: guest.isForeign
          })
        )
        payload.callback()
      })
    }

    const { data } = await sendRegistrationEmail({
      guest,
      isUniquePass,
      email: payload.email,
      isForeign: guest.isForeign
    })
    const { id, codigoVerificacion: code } = data.payload

    return batch(() => {
      dispatch(onSendRegistrationEmailSuccess({ id, code, email: payload.email }))
      payload.callback()
    })
  } catch (error) {
    return dispatch(onSendRegistrationEmailError({ error }))
  }
}

export const UPDATE_REGISTRATION_INFORMATION = 'UPDATE_REGISTRATION_INFORMATION'
export const UPDATE_REGISTRATION_INFORMATION_ERROR = 'UPDATE_REGISTRATION_INFORMATION_ERROR'
export const UPDATE_REGISTRATION_INFORMATION_SUCCESS = 'UPDATE_REGISTRATION_INFORMATION_SUCCESS'
export const onUpdateRegistrationInformation = makeActionCreator(UPDATE_REGISTRATION_INFORMATION)
export const onUpdateRegistrationInformationError = makeActionCreator(UPDATE_REGISTRATION_INFORMATION_ERROR, 'payload')
export const onUpdateRegistrationInformationSuccess = makeActionCreator(
  UPDATE_REGISTRATION_INFORMATION_SUCCESS,
  'payload'
)
export const onUpdateRegistrationInformationThunk =
  ({ email }) =>
  async (dispatch, getState) => {
    dispatch(onUpdateRegistrationInformation())

    try {
      const { id } = getState().registerWizard.guest

      await updateRegistrationInformation({ id, email })
      return dispatch(onUpdateRegistrationInformationSuccess())
    } catch (error) {
      return dispatch(onUpdateRegistrationInformationError({ error }))
    }
  }

export const RESEND_REGISTRATION_EMAIL = 'RESEND_REGISTRATION_EMAIL'
export const RESEND_REGISTRATION_EMAIL_ERROR = 'RESEND_REGISTRATION_EMAIL_ERROR'
export const RESEND_REGISTRATION_EMAIL_SUCCESS = 'RESEND_REGISTRATION_EMAIL_SUCCESS'
export const onResendRegistrationEmail = makeActionCreator(RESEND_REGISTRATION_EMAIL)
export const onResendRegistrationEmailError = makeActionCreator(RESEND_REGISTRATION_EMAIL_ERROR, 'payload')
export const onResendRegistrationEmailSuccess = makeActionCreator(RESEND_REGISTRATION_EMAIL_SUCCESS, 'payload')
export const onResendRegistrationEmailThunk = payload => async (dispatch, getState) => {
  dispatch(onResendRegistrationEmail())

  try {
    const { id } = getState().registerWizard.guest

    if (isLocalEnv) {
      return batch(() => {
        dispatch(
          onResendRegistrationEmailSuccess({
            id: 'local-resend-email-test',
            code: '1234',
            email: 'dccp@chilecompra.cl'
          })
        )
        payload.callback()
      })
    }

    const { data } = await resendRegistrationEmail({ id })
    const { codigoVerificacion: code } = data.payload

    return batch(() => {
      dispatch(onResendRegistrationEmailSuccess({ code }))
      payload.callback()
    })
  } catch (error) {
    return dispatch(onResendRegistrationEmailError({ error }))
  }
}

export const REGISTER_CODE_VALIDATION = ' REGISTER_CODE_VALIDATION'
export const REGISTER_CODE_VALIDATION_ERROR = ' REGISTER_CODE_VALIDATION_ERROR'
export const REGISTER_CODE_VALIDATION_SUCCESS = ' REGISTER_CODE_VALIDATION_SUCCESS'
export const onRegisterCodeValidation = makeActionCreator(REGISTER_CODE_VALIDATION)
export const onRegisterCodeValidationError = makeActionCreator(REGISTER_CODE_VALIDATION_ERROR, 'payload')
export const onRegisterCodeValidationSuccess = makeActionCreator(REGISTER_CODE_VALIDATION_SUCCESS, 'payload')
export const onRegisterCodeValidationThunk =
  ({ code }) =>
  async (dispatch, getState) => {
    dispatch(onRegisterCodeValidation())

    try {
      const { id } = getState().registerWizard.guest

      const { data } = await codeValidation({ id, code })
      const { estado: isValid } = data.payload

      if (isValid) {
        return dispatch(onRegisterCodeValidationSuccess())
      } else {
        return dispatch(onRegisterCodeValidationError())
      }
    } catch (error) {
      return dispatch(onRegisterCodeValidationError({ error }))
    }
  }

export const UPDATE_PASSWORD = 'UPDATE_PASSWORD'
export const UPDATE_PASSWORD_ERROR = 'UPDATE_PASSWORD_ERROR'
export const UPDATE_PASSWORD_SUCCESS = 'UPDATE_PASSWORD_SUCCESS'
export const onUpdatePassword = makeActionCreator(UPDATE_PASSWORD)
export const onUpdatePasswordError = makeActionCreator(UPDATE_PASSWORD_ERROR, 'payload')
export const onUpdatePasswordSuccess = makeActionCreator(UPDATE_PASSWORD_SUCCESS, 'payload')
export const onUpdatePasswordThunk = payload => async (dispatch, getState) => {
  dispatch(onUpdatePassword())

  try {
    const { id } = getState().registerWizard.guest
    const { data } = await updatePassword({ id, password: payload.password })

    return batch(() => {
      dispatch(onUpdatePasswordSuccess({ id: data.payload.id || id }))
      payload.callback()
    })
  } catch (error) {
    return dispatch(onSendRegistrationEmailError({ error }))
  }
}

export const RESET_REGISTER_WIZARD_STATE = 'RESET_REGISTER_WIZARD_STATE'
export const onResetRegisterWizardState = makeActionCreator(RESET_REGISTER_WIZARD_STATE)

export const SET_REGISTER_TYPE = 'SET_REGISTER_TYPE'
export const onRegisterBy = makeActionCreator(SET_REGISTER_TYPE, 'payload')

export const CLEAN_GUEST_BUSINESS_ERRORS_SUCCESS = 'CLEAN_GUEST_BUSINESS_ERRORS_SUCCESS'
export const onCleanGuestBusinessErrorsSuccess = makeActionCreator(CLEAN_GUEST_BUSINESS_ERRORS_SUCCESS)

export const CHECK_GUEST_BUSINESS = 'CHECK_GUEST_BUSINESS'
export const CHECK_GUEST_BUSINESS_ERROR = 'CHECK_GUEST_BUSINESS_ERROR'
export const CHECK_GUEST_BUSINESS_SUCCESS = 'CHECK_GUEST_BUSINESS_SUCCESS'
export const onCheckGuestBusiness = makeActionCreator(CHECK_GUEST_BUSINESS, 'payload')
export const onCheckGuestBusinessError = makeActionCreator(CHECK_GUEST_BUSINESS_ERROR, 'payload')
export const onCheckGuestBusinessSuccess = makeActionCreator(CHECK_GUEST_BUSINESS_SUCCESS, 'payload')
export const onCheckGuestBusinessThunk = payload => async (dispatch, getState) => {
  dispatch(onCheckGuestBusiness({ dni: payload.dni }))

  try {
    const { guest } = getState().registerWizard
    const formattedDNI = formatDNI(payload.dni, 2)
    const { data: businessData } = await getSellerBusiness({ dni: formattedDNI }).catch(({ originalError }) => {
      throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR', originalError })
    })
    const isNaturalEntity = businessData.payload?.tipoContribuyenteCod === 1
    const entityType = isNaturalEntity ? 'natural' : 'legal'
    const isPublicOrg =
      businessData.payload?.tipoContribuyenteCod === 5 || businessData.payload?.tipoContribuyenteCod === 6 ? 1 : 0

    if (isNaturalEntity && ffTaxesKeyNaturalChecks && payload.dni !== guest.dni) {
      throw customizeError({ code: 400, reason: 'DNI_DOES_NOT_MATCH_ERROR' })
    }

    if (isEnableCivilRegistry && isNaturalEntity) {
      const { data: businessAliveData } = await getIdentity({ dni: formattedDNI }).catch(({ originalError }) => {
        const apiData = originalError.response?.data
        const rawCode = getErrorCode(apiData)
        if (rawCode && rawCode === '400-6') {
          throw customizeError({ code: 400, reason: 'DNI_BELONGS_TO_A_DECEASED_ERROR', originalError })
        }
        throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR', originalError })
      })

      const {
        fechaDefuncion: deathDateString,
        fechaDeMuertePresunta: presumedDeathDateString,
        fechaNacimiento: birthDateString
      } = businessAliveData.payload || {}
      const deathDate =
        deathDateString && deathDateString !== '0000-00-00' ? new Date(deathDateString) : addDays(new Date(), 1)
      const presumedDeathDate =
        presumedDeathDateString && presumedDeathDateString !== '0000-00-00'
          ? new Date(presumedDeathDateString)
          : addDays(new Date(), 1)
      const businessIsDead = isBefore(deathDate, new Date()) && isBefore(presumedDeathDate, new Date())
      const isMinor = checkIfIsMinor(birthDateString)

      if (businessIsDead) {
        throw customizeError({ code: 400, reason: 'DNI_BELONGS_TO_A_DECEASED_ERROR' })
      }

      if (isMinor) {
        throw customizeError({ code: 400, reason: 'DNI_BELONGS_TO_A_MINOR_ERROR' })
      }
    }

    const { data: sellerBlacklistedData } = await checkSellerIsBlacklisted({ dni: formattedDNI }).catch(
      ({ originalError }) => {
        throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR', originalError })
      }
    )

    if (sellerBlacklistedData.payload?.estaEnListaNegra) {
      throw customizeError({ code: 400, reason: 'DNI_IS_BLACKLISTED_ERROR' })
    }

    const { data: sellerData } = await getSeller({ dni: payload.dni }).catch(({ originalError }) => {
      throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR', originalError })
    })

    if (sellerData.payload?.proveedor) {
      throw customizeError({ code: 400, reason: 'DNI_ALREADY_LINKED_ERROR' })
    }

    const { data: sellerActivitiesData } = await getSellerActivities({ dni: payload.dni }).catch(
      ({ originalError }) => {
        throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR', originalError })
      }
    )

    if (!sellerActivitiesData.payload?.estado) {
      throw customizeError({ code: 400, reason: 'DNI_NOT_INITIALIZED_ERROR' })
    }

    const { apellidoMaterno, apellidoPaterno, nombre, razonSocial } = businessData.payload || {}

    return batch(() => {
      dispatch(
        onCheckGuestBusinessSuccess({
          entityType,
          isPublicOrg,
          name: entityType === 'legal' ? razonSocial : `${nombre} ${apellidoPaterno} ${apellidoMaterno}`,
          dni: payload.dni
        })
      )

      if (entityType === 'legal') {
        return
      }

      payload.callback()
    })
  } catch (error) {
    return dispatch(onCheckGuestBusinessError(error.toObject()))
  }
}

export const GET_GUEST_BUSINESS_OPTIONS = 'GET_GUEST_BUSINESS_OPTIONS'
export const GET_GUEST_BUSINESS_OPTIONS_ERROR = 'GET_GUEST_BUSINESS_OPTIONS_ERROR'
export const GET_GUEST_BUSINESS_OPTIONS_SUCCESS = 'GET_GUEST_BUSINESS_OPTIONS_SUCCESS'
export const onGetGuestBusinessOptions = makeActionCreator(GET_GUEST_BUSINESS_OPTIONS)
export const onGetGuestBusinessOptionsError = makeActionCreator(GET_GUEST_BUSINESS_OPTIONS_ERROR, 'payload')
export const onGetGuestBusinessOptionsSuccess = makeActionCreator(GET_GUEST_BUSINESS_OPTIONS_SUCCESS, 'payload')
export const onGetGuestBusinessOptionsThunk = () => async (dispatch, getState) => {
  dispatch(onGetGuestBusinessOptions())

  try {
    const { business } = getState().registerWizard
    const initialYear = getYear(addYears(new Date(), -1))
    const maxYear = getYear(addYears(new Date(), -2))
    const formattedDNI = formatDNI(business.dni, 2)
    const getBusinessIncome = business.entityType === 'legal' ? getSellerIncomeSales : getSellerIncomeBracket
    const getRecursiveBusinessIncome = (dni, year) =>
      getBusinessIncome({ dni, year })
        .then(response => [response, year])
        .catch(error => {
          if (year > maxYear) {
            return getRecursiveBusinessIncome(dni, year - 1)
          }

          throw error
        })
    const [
      { data: businessAddressData },
      { data: businessIncomeBracketsData },
      [{ data: businessIncomeData }, businessIncomeYear],
      { data: businessStatesData }
    ] = await Promise.all([
      getSellerAddress({ dni: formattedDNI }).catch(() => ({ data: {} })),
      getBusinessIncomeBrackets().catch(() => ({ data: {} })),
      getRecursiveBusinessIncome(formattedDNI, initialYear).catch(() => [{ data: {} }, initialYear]),
      getStates().catch(() => ({ data: {} }))
    ])
    const [addressDictionary = {}] = businessAddressData.payload?.direcciones?.direccion || []

    return batch(() => {
      dispatch(
        onGetGuestBusinessOptionsSuccess({
          address: [
            addressDictionary.calle && addressDictionary.calle !== 'No hay Dato' && addressDictionary.calle,
            addressDictionary.numero && addressDictionary.numero !== 'No hay Dato' && ` ${addressDictionary.numero}`,
            addressDictionary.departamento &&
              addressDictionary.departamento !== 'No hay Dato' &&
              `, departamento ${addressDictionary.departamento}`,
            addressDictionary.villapoblacion &&
              addressDictionary.villapoblacion !== 'No hay Dato' &&
              `, ${addressDictionary.villapoblacion}`
          ]
            .filter(Boolean)
            .join('')
            .toUpperCase(),
          district: parseInt(addressDictionary.comuna, 10),
          incomeBracket: businessIncomeData.payload?.tramo,
          incomeBrackets: businessIncomeBracketsData.payload?.map(income => ({
            name: income.nomTramo,
            value: income.id
          })),
          incomeYear: businessIncomeYear,
          size: businessIncomeData.payload?.tamano,
          state: parseInt(addressDictionary.region, 10),
          states: businessStatesData.payload?.map(state => ({ name: state.nombre, value: state.id })),
          type: business.entityType === 'natural' ? 21 : 22
        })
      )

      if (addressDictionary.region) {
        dispatch(onGuestBusinessDistrictsThunk({ stateID: parseInt(addressDictionary.region, 10) }))
      }
    })
  } catch (error) {
    return dispatch(onGetGuestBusinessOptionsError(error))
  }
}

export const GET_GUEST_BUSINESS_DISTRICTS = 'GET_GUEST_BUSINESS_DISTRICTS'
export const GET_GUEST_BUSINESS_DISTRICTS_ERROR = 'GET_GUEST_BUSINESS_DISTRICTS_ERROR'
export const GET_GUEST_BUSINESS_DISTRICTS_SUCCESS = 'GET_GUEST_BUSINESS_DISTRICTS_SUCCESS'
export const onGuestBusinessDistricts = makeActionCreator(GET_GUEST_BUSINESS_DISTRICTS)
export const onGuestBusinessDistrictsError = makeActionCreator(GET_GUEST_BUSINESS_DISTRICTS_ERROR, 'payload')
export const onGuestBusinessDistrictsSuccess = makeActionCreator(GET_GUEST_BUSINESS_DISTRICTS_SUCCESS, 'payload')
export const onGuestBusinessDistrictsThunk = payload => async dispatch => {
  dispatch(onGuestBusinessDistricts())

  try {
    const { data } = await getDistricts({ stateID: payload.stateID })

    return dispatch(
      onGuestBusinessDistrictsSuccess({
        districts: data.payload?.map(district => ({ id: district.id, name: district.nombre, value: district.idSii }))
      })
    )
  } catch (error) {
    return dispatch(onGuestBusinessDistrictsError(error))
  }
}

export const CLEAN_GUEST_BUSINESS_SUCCESS = 'CLEAN_GUEST_BUSINESS_SUCCESS'
export const onCleanGuestBusinessSuccess = makeActionCreator(CLEAN_GUEST_BUSINESS_SUCCESS)
export const onCleanGuestBusinessThunk = payload => dispatch =>
  batch(() => {
    dispatch(onCleanGuestBusinessSuccess())
    payload.callback()
  })

export const GET_GUEST_BUSINESS_AREAS = 'GET_GUEST_BUSINESS_AREAS'
export const GET_GUEST_BUSINESS_AREAS_ERROR = 'GET_GUEST_BUSINESS_AREAS_ERROR'
export const GET_GUEST_BUSINESS_AREAS_SUCCESS = 'GET_GUEST_BUSINESS_AREAS_SUCCESS'
export const onGetGuestBusinessAreas = makeActionCreator(GET_GUEST_BUSINESS_AREAS)
export const onGetGuestBusinessAreasError = makeActionCreator(GET_GUEST_BUSINESS_AREAS_ERROR, 'payload')
export const onGetGuestBusinessAreasSuccess = makeActionCreator(GET_GUEST_BUSINESS_AREAS_SUCCESS, 'payload')
export const onGetGuestBusinessAreasThunk = payload => async dispatch => {
  dispatch(onGetGuestBusinessAreas())

  try {
    const { data } = await getBusinessAreas({ actionType: GET_GUEST_BUSINESS_AREAS, query: payload.query })

    return dispatch(
      onGetGuestBusinessAreasSuccess({
        areas: data.payload.map(area => ({
          name: area.categoria,
          value: area.id
        }))
      })
    )
  } catch (error) {
    return dispatch(onGetGuestBusinessAreasError(error))
  }
}

export const CREATE_TEMPORARY_SELLER = 'CREATE_TEMPORARY_SELLER'
export const CREATE_TEMPORARY_SELLER_ERROR = 'CREATE_TEMPORARY_SELLER_ERROR'
export const CREATE_TEMPORARY_SELLER_SUCCESS = 'CREATE_TEMPORARY_SELLER_SUCCESS'
export const onCreateTemporarySeller = makeActionCreator(CREATE_TEMPORARY_SELLER)
export const onCreateTemporarySellerError = makeActionCreator(CREATE_TEMPORARY_SELLER_ERROR, 'payload')
export const onCreateTemporarySellerSuccess = makeActionCreator(CREATE_TEMPORARY_SELLER_SUCCESS, 'payload')
export const onCreateTemporarySellerThunk = payload => async (dispatch, getState) => {
  dispatch(onCreateTemporarySeller())

  try {
    const { business, guest } = getState().registerWizard
    const { data } = await createTemporarySeller({
      address: payload.address,
      conformation: payload.conformation ? parseInt(payload.conformation, 10) : undefined,
      country: payload.country ? parseInt(payload.country, 10) : undefined,
      district: business.districts?.find(district => district.value === payload.district).id,
      dni: business.dni || payload.dni,
      guestID: guest.id,
      incomeBracket: payload.incomeBracket ? parseInt(payload.incomeBracket, 10) : undefined,
      incomeYear: business.incomeYear || getYear(addYears(new Date(), -1)),
      isForeign: payload.isForeign,
      name: business.name || payload.name,
      nickname: payload.nickname,
      size: payload.size ? parseInt(payload.size, 10) : undefined,
      state: payload.state,
      type: business.type || payload.type,
      isPublicOrg: business.isPublicOrg || 0
    })

    return batch(() => {
      if (payload.bypassNextStep) {
        return dispatch(
          onCreateSellerThunk({
            businessID: data.payload?.idEmpresa,
            callback: payload.callback
          })
        )
      }

      dispatch(onCreateTemporarySellerSuccess({ id: data.payload?.idEmpresa }))
      payload.callback()
    })
  } catch (error) {
    const foreignProviderDetectedAsNationalError = error?.originalError?.response?.data?.errores?.find(
      availableError => availableError?.codigo === '400-3'
    )
    const businessAlreadyExistsError = error?.originalError?.response?.data?.errores?.find(
      availableError => availableError?.codigo === '400-1'
    )

    if (businessAlreadyExistsError) {
      return dispatch(onCreateTemporarySellerError({ reason: 'BUSINESS_ALREADY_EXISTS_ERROR' }))
    }

    if (foreignProviderDetectedAsNationalError) {
      return dispatch(onCreateTemporarySellerError({ reason: 'BUSINESS_IS_NOT_FOREIGNER_ERROR' }))
    }

    return dispatch(onCreateTemporarySellerError(error.toObject()))
  }
}

export const CREATE_TEMPORARY_SELLER_AREAS = 'CREATE_TEMPORARY_SELLER_AREAS'
export const CREATE_TEMPORARY_SELLER_AREAS_ERROR = 'CREATE_TEMPORARY_SELLER_AREAS_ERROR'
export const onCreateTemporarySellerAreas = makeActionCreator(CREATE_TEMPORARY_SELLER_AREAS)
export const onCreateTemporarySellerAreasError = makeActionCreator(CREATE_TEMPORARY_SELLER_AREAS_ERROR, 'payload')
export const onCreateTemporarySellerAreasThunk = payload => async (dispatch, getState) => {
  dispatch(onCreateTemporarySellerAreas())

  try {
    const { business } = getState().registerWizard

    if (payload.bypassStep) {
      return dispatch(
        onCreateSellerThunk({
          businessID: business.id,
          callback: payload.callback
        })
      )
    }

    const { data: temporarySellerData } = await createTemporarySellerAreas({
      areas: payload.areas,
      businessID: business.id
    })

    return dispatch(
      onCreateSellerThunk({
        businessID: temporarySellerData.payload?.idEmpresa,
        callback: payload.callback
      })
    )
  } catch (error) {
    return dispatch(onCreateTemporarySellerAreasError(error))
  }
}

export const CREATE_SELLER = 'CREATE_SELLER'
export const CREATE_SELLER_ERROR = 'CREATE_SELLER_ERROR'
export const CREATE_SELLER_SUCCESS = 'CREATE_SELLER_SUCCESS'
export const onCreateSeller = makeActionCreator(CREATE_SELLER)
export const onCreateSellerError = makeActionCreator(CREATE_SELLER_ERROR, 'payload')
export const onCreateSellerSuccess = makeActionCreator(CREATE_SELLER_SUCCESS, 'payload')
export const onCreateSellerThunk = payload => async (dispatch, getState) => {
  dispatch(onCreateSeller())

  try {
    const strAffidavitTempSigned = localStorage.getItem('affidavitTempSigned')
    const { data: sellerData } = await createSeller({ businessID: payload.businessID })
    const { business } = getState().registerWizard

    if (strAffidavitTempSigned) {
      const affidavitTempSigned = JSON.parse(strAffidavitTempSigned)
      await signAffidavit({ ...affidavitTempSigned }).finally(() => {
        localStorage.removeItem('affidavitTempSigned')
        deleteCookie('dniRegister')
      })
    }

    const landingData = JSON.stringify({ completed: false })

    const now = new Date()
    const expiryDate = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate(), 0, 0, 0, 0)
    setItemWithExpiryAt(CACHE_CONFIG.LANDING.key, landingData, expiryDate, business.dni)

    const overlayData = JSON.stringify({ isRequiredOverlay: true })
    setItem(CACHE_CONFIG.OVERLAY.key, overlayData, business.dni)

    return batch(() => {
      dispatch(
        onCreateSellerSuccess({
          id: payload.businessID,
          url: sellerData.payload?.url,
          username: sellerData.payload?.usuario
        })
      )
      payload.callback()
    })
  } catch (error) {
    return dispatch(onCreateSellerError(error))
  }
}

export const RESET_SEND_REGISTRATION_EMAIL = 'RESET_SEND_REGISTRATION_EMAIL'
export const onResetSendRegistrationEmail = makeActionCreator(RESET_SEND_REGISTRATION_EMAIL)

export const SET_FOREIGN_REGISTRATION = 'SET_FOREIGN_REGISTRATION'
export const onSetForeignRegistration = makeActionCreator(SET_FOREIGN_REGISTRATION, 'payload')

export const GET_COUNTRIES = 'GET_COUNTRIES'
export const GET_COUNTRIES_ERROR = 'GET_COUNTRIES_ERROR'
export const GET_COUNTRIES_SUCCESS = 'GET_COUNTRIES_SUCCESS'
export const onGetCountries = makeActionCreator(GET_COUNTRIES)
export const onGetCountriesError = makeActionCreator(GET_COUNTRIES_ERROR, 'payload')
export const onGetCountriesSuccess = makeActionCreator(GET_COUNTRIES_SUCCESS, 'payload')
export const onGetCountriesThunk = payload => async dispatch => {
  dispatch(onGetCountries())

  try {
    const { data } = await getCountries({ order: payload.order })

    const countries = data.payload
      ?.map(country => ({
        id: country.id,
        active: country.active,
        code: country.code,
        spanishName: country.name,
        englishName: country.englishName,
        phoneAreaCode: country.phoneCode
      }))
      .filter(country => country.phoneAreaCode !== null)

    return dispatch(onGetCountriesSuccess({ countries }))
  } catch (error) {
    return dispatch(onGetCountriesError({ error }))
  }
}

export const OPEN_CONFIRM_NEW_AFFIDAVIT_DIALOG = 'OPEN_CONFIRM_NEW_AFFIDAVIT_DIALOG'
export const onOpenConfirmNewAffidavitDialog = makeActionCreator(OPEN_CONFIRM_NEW_AFFIDAVIT_DIALOG)

export const CLOSE_CONFIRM_NEW_AFFIDAVIT_DIALOG = 'CLOSE_CONFIRM_NEW_AFFIDAVIT_DIALOG'
export const onCloseConfirmNewAffidavitDialog = makeActionCreator(CLOSE_CONFIRM_NEW_AFFIDAVIT_DIALOG)

export const GET_AFFIDAVIT = 'GET_AFFIDAVIT'
export const GET_AFFIDAVIT_ERROR = 'GET_AFFIDAVIT_ERROR'
export const GET_AFFIDAVIT_SUCCESS = 'GET_AFFIDAVIT_SUCCESS'
export const onGetAffidavit = makeActionCreator(GET_AFFIDAVIT)
export const onGetAffidavitError = makeActionCreator(GET_AFFIDAVIT_ERROR, 'payload')
export const onGetAffidavitSuccess = makeActionCreator(GET_AFFIDAVIT_SUCCESS, 'payload')
export const onGetAffidavitThunk =
  ({ onSuccessCallback = () => undefined }) =>
  async (dispatch, getState) => {
    dispatch(onGetAffidavit())
    try {
      const { business } = getState().registerWizard
      const strTempSigned = localStorage.getItem('affidavitTempSigned')
      const tempSigned = strTempSigned ? JSON.parse(strTempSigned) : undefined
      const naturalPerson = business?.type === 21
      if ((strTempSigned && tempSigned?.businessName) || naturalPerson) {
        return batch(() => {
          onGetAffidavitSuccess({ affidavit: { businessName: tempSigned?.businessName } })
          onSuccessCallback()
        })
      }
      if (!strTempSigned) {
        return dispatch(onOpenConfirmNewAffidavitDialog())
      }
      return dispatch(onGetAffidavitError({ reason: 'NOT_FOUND' }))
    } catch (error) {
      return dispatch(onGetAffidavitError({ reason: error }))
    }
  }

export const GET_SELLER_BLOCKED_INFO = 'GET_SELLER_BLOCKED_INFO'
export const GET_SELLER_BLOCKED_INFO_ERROR = 'GET_SELLER_BLOCKED_INFO_ERROR'
export const GET_SELLER_BLOCKED_INFO_SUCCESS = 'GET_SELLER_BLOCKED_INFO_SUCCESS'
export const onGetSellerBlockedInfo = makeActionCreator(GET_SELLER_BLOCKED_INFO)
export const onGetSellerBlockedInfoError = makeActionCreator(GET_SELLER_BLOCKED_INFO_ERROR, 'payload')
export const onGetSellerBlockedInfoSuccess = makeActionCreator(GET_SELLER_BLOCKED_INFO_SUCCESS, 'payload')
export const onGetSellerBlockedInfoThunk = payload => async dispatch => {
  const {
    checkGuestBusinessCallback = () => undefined,
    checkPersonInformationCallback = () => undefined,
    createTemporarySellerCallback = () => undefined,
    dni = '',
    step = ''
  } = payload

  dispatch(onGetSellerBlockedInfo())

  try {
    const { data } = await postBlockedInformation({
      actionType: GET_SELLER_BLOCKED_INFO,
      dni,
      isForeign: checkIfForeignDni(dni) ? 1 : 0,
      isLog: 1,
      module: 'INSCRIPCIÓN'
    })

    dispatch(
      onGetSellerBlockedInfoSuccess({
        dateBlocked: data.payload?.fechaFinBloqueo || '',
        isBlocked: data.payload?.bloqueado,
        reason: data.payload?.motivo || ''
      })
    )

    if (!data.payload?.bloqueado) {
      if (step !== 'business-info') {
        return checkIfForeignDni(dni) ? checkPersonInformationCallback() : checkGuestBusinessCallback()
      }

      if (step === 'business-info') return createTemporarySellerCallback()
    }
  } catch (error) {
    const { originalError } = error
    const apiData = originalError.response?.data
    const rawCode = getErrorCode(apiData)

    if (rawCode) {
      const [descPart, valPart] = rawCode.split('->').map(str => str.trim())
      if (descPart === 'RUT NO VALIDO')
        return dispatch(
          onGetSellerBlockedInfoError({
            error: customizeError({
              originalError,
              code: valPart,
              reason: 'DNI_INVALID_ERROR'
            }).toObject()
          })
        )
    }

    dispatch(
      onGetSellerBlockedInfoError({
        error: customizeError({
          originalError: error?.originalError,
          code: error?.code,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}
